import Session from 'supertokens-web-js/recipe/session';

const API_URL = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
}

interface ApiError {
  error: string;
  message: string;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async getToken(): Promise<string | null> {
    try {
      if (await Session.doesSessionExist()) {
        return await Session.getAccessToken() || null;
      }
    } catch {
      // Session not available
    }
    return null;
  }

  async request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {} } = options;

    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    };

    const token = await this.getToken();
    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorBody: Record<string, any> = await response.json().catch(() => ({
        error: 'Network Error',
        message: `HTTP ${response.status}`,
      }));
      // Surface Zod validation issues array if present (site wizard etc.)
      if (errorBody.issues && Array.isArray(errorBody.issues) && errorBody.issues.length > 0) {
        throw new Error(errorBody.issues.join('\n'));
      }
      // Carry the API's machine-readable error code (and HTTP status) on the
      // thrown Error so callers can map known codes (e.g. `dates_unavailable`)
      // to localized messages instead of rendering the raw string.
      const err = new Error(errorBody.message || errorBody.error) as Error & {
        code?: string;
        status?: number;
      };
      if (typeof errorBody.code === 'string') err.code = errorBody.code;
      err.status = response.status;
      throw err;
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  get<T>(endpoint: string, headers?: Record<string, string>) {
    return this.request<T>(endpoint, { method: 'GET', headers });
  }

  post<T>(endpoint: string, body?: unknown, headers?: Record<string, string>) {
    return this.request<T>(endpoint, { method: 'POST', body, headers });
  }

  put<T>(endpoint: string, body?: unknown, headers?: Record<string, string>) {
    return this.request<T>(endpoint, { method: 'PUT', body, headers });
  }

  delete<T>(endpoint: string, headers?: Record<string, string>) {
    return this.request<T>(endpoint, { method: 'DELETE', headers });
  }

  patch<T>(endpoint: string, body?: unknown, headers?: Record<string, string>) {
    return this.request<T>(endpoint, { method: 'PATCH', body, headers });
  }

  /** Raw bytes as the request body (one piece of a large file); the reply is JSON. */
  async putBytes<T>(endpoint: string, body: Blob): Promise<T> {
    const token = await this.getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/octet-stream' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${this.baseUrl}${endpoint}`, { method: 'PUT', headers, body, credentials: 'include' });
    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({ error: 'Upload Error', message: `HTTP ${response.status}` }));
      throw new Error(error.message || error.error);
    }
    return response.json();
  }

  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const token = await this.getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        error: 'Upload Error',
        message: `HTTP ${response.status}`,
      }));
      throw new Error(error.message || error.error);
    }

    return response.json();
  }
}

export const api = new ApiClient(API_URL);
export default api;
